import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { ArrowLeft, Sparkles, Flag, ChevronRight } from "lucide-react";

export default function Studio() {
  const navigate = useNavigate();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [v1Res, v2Res] = await Promise.all([
        fetch(`${API}/rounds`, { credentials: "include" }),
        fetch(`${API}/v2/rounds`, { credentials: "include" }),
      ]);
      const v1 = v1Res.ok ? await v1Res.json() : [];
      const v2 = v2Res.ok ? await v2Res.json() : [];
      const merged = [...v2, ...v1]
        .filter((r) => r.status === "completed" || r.status === "in_progress")
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setRounds(merged);
    } catch {
      setRounds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F7F3EA] text-[#102E32]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="studio-hub-page">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DED5C2] bg-white">
        <button onClick={() => navigate("/dashboard")} className="text-[#6B7A6F] hover:text-[#102E32]" data-testid="studio-back-btn"><ArrowLeft size={20} /></button>
        <span className="font-extrabold">CADii Studio</span>
        <span className="w-5" />
      </div>

      <div className="max-w-lg mx-auto px-4 pt-8 pb-16">
        <h2 className="text-3xl font-black tracking-tight">Crea imágenes y videos</h2>
        <p className="text-sm text-[#64748B] mt-1 mb-6">Comparte tu golf con plantillas CADii</p>

        {/* Free content */}
        <button
          onClick={() => navigate("/social/free")}
          className="w-full flex items-center gap-4 rounded-2xl bg-[#103B31] text-white p-5 mb-8 hover:bg-[#092B25] transition-all active:scale-[0.98]"
          style={{ boxShadow: "0 8px 24px rgba(16,46,50,.14)" }}
          data-testid="studio-free-btn"
        >
          <div className="w-13 h-13 rounded-full bg-white/10 flex items-center justify-center p-3.5">
            <Sparkles size={24} />
          </div>
          <div className="flex-1 text-left">
            <p className="text-lg font-black">Contenido libre</p>
            <p className="text-xs text-white/70">Sube tu foto y personaliza el texto</p>
          </div>
          <ChevronRight size={20} className="text-white/70" />
        </button>

        <p className="text-[10px] uppercase tracking-widest text-[#9AA69C] font-bold mb-3">Desde una ronda</p>
        {loading ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-3 border-[#103B31] border-t-transparent rounded-full animate-spin" /></div>
        ) : rounds.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center" data-testid="studio-no-rounds">
            <p className="text-sm text-gray-500 font-medium">Sin rondas todavía</p>
            <p className="text-xs text-gray-400 mt-1">Juega una ronda para crear contenido con tus datos</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rounds.map((r) => (
              <button key={r.round_id} onClick={() => navigate(`/social/${r.round_id}`)}
                className="w-full flex items-center gap-3 bg-white rounded-2xl border border-gray-100 p-3.5 text-left hover:shadow-md transition-shadow"
                data-testid={`studio-round-${r.round_id}`}>
                <div className="w-9 h-9 rounded-xl bg-[#103B31]/5 flex items-center justify-center flex-shrink-0">
                  <Flag className="w-4 h-4 text-[#103B31]" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{r.name}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {r.date}{(typeof r.course === "object" ? r.course?.course_name : r.course) ? ` · ${typeof r.course === "object" ? r.course?.course_name : r.course}` : ""}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
